/**
 * Terreno terrestre das rotas (PNG + map JSON).
 * Água é ground Myst (ex.: shallow water 4820); espuma de margem fica no stack sobre terra.
 *
 * O PNG cobre (max−min+1) tiles em cada eixo — o span inclusivo precisa do +1
 * para left%/top% baterem com o tile real.
 */

const WATER_GROUND_IDS = new Set([
  493,
  4608, 4609, 4613, 4615, 4616, 4619,
  4820, 4821, 4822, 4823, 4824, 4825,
  4835, 4837,
]);

/** Escala das rotas PNG antigas; velocidades/ranges existentes usam este espaço. */
const LEGACY_SPAN_X = 37;
const LEGACY_SPAN_Y = 33;

export function isWaterGroundId(id) {
  return WATER_GROUND_IDS.has(Number(id));
}

function spanSize(min, max) {
  return Math.max(1, Number(max) - Number(min) + 1);
}

export function routeMetricScale(index) {
  if (!index) return { x: 1, y: 1 };
  return {
    x: spanSize(index.minX, index.maxX) / LEGACY_SPAN_X,
    y: spanSize(index.minY, index.maxY) / LEGACY_SPAN_Y,
  };
}

/** Distância compatível com os ranges/velocidades do mapa antigo. */
export function routeDistance(fromX, fromY, toX, toY, index) {
  const scale = routeMetricScale(index);
  return Math.hypot((toX - fromX) * scale.x, (toY - fromY) * scale.y);
}

/**
 * Move uma distância no espaço métrico legado e devolve percentuais do
 * mapa atual. Assim um mapa 10x maior não faz os atores correrem 10x mais.
 */
export function stepTowardRoute(fromX, fromY, toX, toY, step, index) {
  const scale = routeMetricScale(index);
  const dx = (toX - fromX) * scale.x;
  const dy = (toY - fromY) * scale.y;
  const distance = Math.hypot(dx, dy);
  if (!Number.isFinite(distance) || distance <= 0) return [fromX, fromY];
  const amount = Math.min(Math.max(0, Number(step) || 0), distance);
  return [
    fromX + (dx / distance) * amount / scale.x,
    fromY + (dy / distance) * amount / scale.y,
  ];
}

/** Converte um delta/raio legado para os percentuais do mapa atual. */
export function scaleRouteDelta(dx, dy, index) {
  const scale = routeMetricScale(index);
  return [dx / scale.x, dy / scale.y];
}

export function buildRouteLandIndex(mapDoc) {
  const meta = mapDoc?.meta || {};
  const minX = Number(meta.minX);
  const maxX = Number(meta.maxX);
  const minY = Number(meta.minY);
  const maxY = Number(meta.maxY);
  const groundZ = Number(meta.groundZ ?? 7);
  const water = new Set();
  const candidates = [];

  for (const tile of mapDoc?.tiles || []) {
    const [x, y, z, groundId] = tile;
    if (z !== groundZ || !groundId) continue;
    const key = `${x},${y}`;
    if (isWaterGroundId(groundId)) {
      water.add(key);
      continue;
    }
    candidates.push([x, y, key]);
  }

  const land = new Set();
  const landList = [];
  for (const [x, y, key] of candidates) {
    // Mantém 1 tile de folga da água: a arte/PNG às vezes “molha” a margem.
    if (
      water.has(`${x - 1},${y}`) ||
      water.has(`${x + 1},${y}`) ||
      water.has(`${x},${y - 1}`) ||
      water.has(`${x},${y + 1}`)
    ) {
      continue;
    }
    if (land.has(key)) continue;
    land.add(key);
    landList.push([x, y]);
  }

  // Se a margem engolir tudo (mapas quase só lago), cai no terreno seco bruto.
  if (!landList.length) {
    for (const [x, y, key] of candidates) {
      if (land.has(key)) continue;
      land.add(key);
      landList.push([x, y]);
    }
  }

  return { minX, maxX, minY, maxY, land, landList, water };
}

export function percentToTile(xPct, yPct, index) {
  const spanX = spanSize(index.minX, index.maxX);
  const spanY = spanSize(index.minY, index.maxY);
  const tx = index.minX + Math.floor((Number(xPct) / 100) * spanX);
  const ty = index.minY + Math.floor((Number(yPct) / 100) * spanY);
  return [
    Math.max(index.minX, Math.min(index.maxX, tx)),
    Math.max(index.minY, Math.min(index.maxY, ty)),
  ];
}

export function tileToPercent(tx, ty, index) {
  const spanX = spanSize(index.minX, index.maxX);
  const spanY = spanSize(index.minY, index.maxY);
  return [
    ((tx - index.minX + 0.5) / spanX) * 100,
    ((ty - index.minY + 0.5) / spanY) * 100,
  ];
}

export function isLandAtPercent(xPct, yPct, index) {
  if (!index?.land?.size) return true;
  const [tx, ty] = percentToTile(xPct, yPct, index);
  return index.land.has(`${tx},${ty}`);
}

export function constrainToLand(xPct, yPct, index, margin = 8) {
  const clamp = (x, y) => [
    Math.max(margin, Math.min(100 - margin, Number(x) || 50)),
    Math.max(margin, Math.min(100 - margin, Number(y) || 50)),
  ];
  if (!index?.landList?.length) return clamp(xPct, yPct);
  if (isLandAtPercent(xPct, yPct, index)) return clamp(xPct, yPct);

  const [tx, ty] = percentToTile(xPct, yPct, index);
  let best = null;
  let bestD = Infinity;
  for (const [lx, ly] of index.landList) {
    const d = (lx - tx) * (lx - tx) + (ly - ty) * (ly - ty);
    if (d < bestD) {
      bestD = d;
      best = [lx, ly];
    }
  }
  const [px, py] = tileToPercent(best[0], best[1], index);
  return clamp(px, py);
}

export function pickRandomLandPercent(index, margin = 8) {
  if (!index?.landList?.length) return [50, 50];
  const [tx, ty] = index.landList[Math.floor(Math.random() * index.landList.length)];
  return constrainToLand(...tileToPercent(tx, ty, index), index, margin);
}

export function stepOnLand(fromX, fromY, toX, toY, index, margin = 8) {
  const clamp = (x, y) => [
    Math.max(margin, Math.min(100 - margin, x)),
    Math.max(margin, Math.min(100 - margin, y)),
  ];
  if (!index?.land?.size) return clamp(toX, toY);
  if (isLandAtPercent(toX, toY, index)) return clamp(toX, toY);
  if (isLandAtPercent(toX, fromY, index)) return clamp(toX, fromY);
  if (isLandAtPercent(fromX, toY, index)) return clamp(fromX, toY);
  return [fromX, fromY];
}

/** Amostra a reta entre dois pontos; true se algum tile no caminho não é terra. */
export function landPathBlocked(fromX, fromY, toX, toY, index, samples = 14) {
  if (!index?.land?.size) return false;
  // Perto o suficiente: não marcar como inacessível (margem/água entre tiles vizinhos).
  if (routeDistance(fromX, fromY, toX, toY, index) <= 8) return false;
  const n = Math.max(4, Math.min(48, Math.trunc(Number(samples) || 14)));
  for (let i = 1; i <= n; i += 1) {
    const t = i / (n + 1);
    const x = fromX + (toX - fromX) * t;
    const y = fromY + (toY - fromY) * t;
    if (!isLandAtPercent(x, y, index)) return true;
  }
  return false;
}

/**
 * Passo de chase que permanece em terra e prioriza reduzir a distância ao alvo.
 * Contorna água/margem em vez de ficar deslizando sem progresso.
 */
export function bestStepTowardLand(fromX, fromY, toX, toY, step, index, margin = 5) {
  const amount = Math.max(0, Number(step) || 0);
  if (amount <= 0) return [fromX, fromY];

  const tryStep = (goalX, goalY) => {
    let [nx, ny] = stepTowardRoute(fromX, fromY, goalX, goalY, amount, index);
    [nx, ny] = stepOnLand(fromX, fromY, nx, ny, index, margin);
    return [nx, ny];
  };

  const score = (nx, ny) => {
    const moved = routeDistance(fromX, fromY, nx, ny, index);
    if (moved <= 0.0001) return -Infinity;
    const remaining = routeDistance(nx, ny, toX, toY, index);
    // Preferir progresso ao alvo; desempate pelo passo maior (menos “tremor”).
    return -remaining * 1000 + moved;
  };

  let best = [fromX, fromY];
  let bestScore = -Infinity;

  const consider = (nx, ny) => {
    const s = score(nx, ny);
    if (s > bestScore) {
      bestScore = s;
      best = [nx, ny];
    }
  };

  consider(...tryStep(toX, toY));

  const ang = Math.atan2(toY - fromY, toX - fromX);
  const probe = Math.max(8, Math.min(22, amount * 4));
  const deltas = [
    Math.PI / 2, -Math.PI / 2,
    Math.PI / 3, -Math.PI / 3,
    Math.PI / 4, -Math.PI / 4,
    2 * Math.PI / 3, -2 * Math.PI / 3,
    Math.PI / 6, -Math.PI / 6,
    5 * Math.PI / 6, -5 * Math.PI / 6,
  ];
  for (const delta of deltas) {
    consider(...tryStep(
      fromX + Math.cos(ang + delta) * probe,
      fromY + Math.sin(ang + delta) * probe,
    ));
  }

  return best;
}
